import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export interface PrescriptionDetail {
  id: string
  prescription_number: number
  patient_id: string
  medication_id: string
  treatment_id: string | null
  veterinarian_id: string | null
  dosage: string
  dosage_calculated: number | null
  dose_rate: number | null
  frequency: string
  route: string
  duration: string
  quantity_dispensed: number
  total_refills: number
  remaining_refills: number
  start_date: string
  end_date: string | null
  status: string
  created_at: string
  updated_at: string
  // Joined fields
  patient_name: string
  patient_weight: number | null
  patient_weight_unit: string
  patient_allergies: string
  medication_name: string
  medication_category: string
  medication_dose_rate: number | null
  medication_interactions: string
  medication_controlled: boolean
  medication_dea_schedule: string | null
  veterinarian_name: string | null
}

export interface PrescriptionRefill {
  id: string
  prescription_id: string
  refill_date: string
  quantity_dispensed: number
  dispensed_by: string
  notes: string
  created_at: string
}

export interface DrugAlert {
  id: string
  prescription_id: string
  patient_id: string
  alert_type: string
  severity: string
  description: string
  conflicting_drugs: string
  acknowledged: boolean
  created_at: string
}

interface PrescriptionsState {
  prescription: PrescriptionDetail | null
  refills: PrescriptionRefill[]
  alerts: DrugAlert[]
  loading: boolean
  error: string | null
}

const initialState: PrescriptionsState = {
  prescription: null,
  refills: [],
  alerts: [],
  loading: false,
  error: null,
}

export const fetchPrescription = createAsyncThunk(
  'prescriptions/fetchOne',
  async (id: string) => {
    const res = await fetch(`/.netlify/functions/prescriptions/${id}`)
    if (!res.ok) throw new Error('Failed to fetch prescription')
    return res.json() as Promise<PrescriptionDetail>
  }
)

export const updatePrescription = createAsyncThunk(
  'prescriptions/update',
  async ({ id, data }: { id: string; data: Partial<PrescriptionDetail> }) => {
    const res = await fetch(`/.netlify/functions/prescriptions/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to update prescription')
    return res.json() as Promise<PrescriptionDetail>
  }
)

export const fetchRefills = createAsyncThunk(
  'prescriptions/fetchRefills',
  async (prescriptionId: string) => {
    const res = await fetch(`/.netlify/functions/prescription-refills?prescription_id=${prescriptionId}`)
    if (!res.ok) throw new Error('Failed to fetch refills')
    return res.json() as Promise<PrescriptionRefill[]>
  }
)

export const addRefill = createAsyncThunk(
  'prescriptions/addRefill',
  async (data: { prescription_id: string; quantity_dispensed: number; dispensed_by: string; notes: string }) => {
    const res = await fetch('/.netlify/functions/prescription-refills', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to add refill')
    return res.json() as Promise<PrescriptionRefill>
  }
)

export const fetchAlerts = createAsyncThunk(
  'prescriptions/fetchAlerts',
  async (prescriptionId: string) => {
    const res = await fetch(`/.netlify/functions/prescription-alerts?prescription_id=${prescriptionId}`)
    if (!res.ok) throw new Error('Failed to fetch alerts')
    return res.json() as Promise<DrugAlert[]>
  }
)

const prescriptionsSlice = createSlice({
  name: 'prescriptions',
  initialState,
  reducers: {
    clearPrescription(state) {
      state.prescription = null
      state.refills = []
      state.alerts = []
      state.loading = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPrescription.pending, (state) => {
        if (!state.prescription) state.loading = true
      })
      .addCase(fetchPrescription.fulfilled, (state, action) => {
        state.prescription = action.payload
        state.loading = false
        state.error = null
      })
      .addCase(fetchPrescription.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to load'
      })
      .addCase(updatePrescription.fulfilled, (state, action) => {
        state.prescription = action.payload
      })
      .addCase(fetchRefills.fulfilled, (state, action) => {
        state.refills = action.payload
      })
      .addCase(addRefill.fulfilled, (state, action) => {
        state.refills.unshift(action.payload)
        if (state.prescription) {
          state.prescription.remaining_refills = Math.max(state.prescription.remaining_refills - 1, 0)
        }
      })
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        state.alerts = action.payload
      })
  },
})

export const { clearPrescription } = prescriptionsSlice.actions
export default prescriptionsSlice.reducer
